'use client';

import { useState, useEffect, useCallback } from 'react';
import {
  BarChart3, ArrowUp, ArrowDown, Loader2, ArrowLeft, Sparkles, RefreshCw,
} from 'lucide-react';
import { ProjectSeoTab } from '@/components/seo/ProjectSeoTab';
import { SeoAssistant } from '@/components/seo/SeoAssistant';

interface SiteOverview {
  domain: string;
  noData?: boolean;
  date?: string;
  clicks: number;
  clicksChange: number;
  impressions: number;
  impressionsChange: number;
  avgPosition: number;
  positionChange: number;
  avgCtr: number;
  topQuery: string;
}

// Pretty site names for the two-line SITE cell (fallback: capitalized domain base).
const SITE_NAMES: Record<string, string> = {
  'bestdecks.ca': 'Best Decks', 'bikeracks.ca': 'Bike Racks',
  'busshelters.ca': 'BusShelters', 'cobbler.ca': 'Cobbler', 'commercialdoors.ca': 'Commercial Doors',
  'crowdcontrol.ca': 'Crowd Control', 'fenced.ca': 'Fenced', 'graffitiremoval.ca': 'Graffiti Removal',
  'mow.ca': 'Mow', 'nametags.ca': 'Nametags', 'porta-potty.ca': 'Porta Potty',
  'skatestopper.ca': 'Skate Stopper', 'snow.ca': 'Snow', 'tore.ca': 'Tore',
  'aviationprivee.fr': 'Aviation Privée', 'yachtcannes.fr': 'Yacht Cannes',
  'yachtmonaco.fr': 'Yacht Monaco', 'yachtsainttropez.fr': 'Yacht Saint-Tropez',
};
function siteName(domain: string): string {
  if (SITE_NAMES[domain]) return SITE_NAMES[domain];
  const base = domain.replace(/\.(ca|fr|com|net)$/, '');
  return base.charAt(0).toUpperCase() + base.slice(1);
}

const PERIODS: { id: string; label: string; days: number }[] = [
  { id: '7d', label: '7d', days: 7 },
  { id: '14d', label: '14d', days: 14 },
  { id: '28d', label: '28d', days: 28 },
  { id: '3mo', label: '3 mo', days: 90 },
];

type SortKey = 'clicks' | 'impressions' | 'avgCtr' | 'avgPosition';
const SORTS: { key: SortKey; label: string }[] = [
  { key: 'clicks', label: 'Clicks' },
  { key: 'impressions', label: 'Impressions' },
  { key: 'avgCtr', label: 'CTR' },
  { key: 'avgPosition', label: 'Avg position' },
];

function fmt(n: number): string {
  const r = Math.round(n * 10) / 10;
  return r % 1 === 0 ? r.toString() : r.toFixed(1);
}

function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

/** Inline change chip next to a value. Renders NOTHING when there's no
 *  change data — Boss: no dangling dashes, keep every row on one line. */
function ChangeUnder({ value, suffix = '%' }: { value: number; suffix?: string }) {
  if (!value) return null;
  const up = value > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${up ? 'text-green-400' : 'text-red-400'}`}>
      {up ? <ArrowUp className="w-2.5 h-2.5" /> : <ArrowDown className="w-2.5 h-2.5" />}
      {Math.abs(value)}{suffix}
    </span>
  );
}

/** Rule-based one-liner per site for the Recommendation column. */
function recommend(s: SiteOverview): { text: string; tone: 'red' | 'amber' | 'green' | 'gray' } {
  if (s.noData) return { text: 'Just added — Google data lands in a few days', tone: 'gray' };
  if (s.clicksChange <= -25) return { text: 'Traffic dropping — investigate top queries', tone: 'red' };
  if (s.avgPosition > 40) return { text: 'Low visibility — strengthen content & links', tone: 'red' };
  if (s.avgPosition <= 10 && s.avgCtr < 3) return { text: 'Page 1 but weak CTR — improve titles & meta', tone: 'amber' };
  if (s.avgPosition > 10 && s.avgPosition <= 20) return { text: 'Push page-2 keywords to page 1', tone: 'amber' };
  if (s.impressions > 10000 && s.avgCtr < 1) return { text: 'High impressions, few clicks — CTR opportunity', tone: 'amber' };
  return { text: 'Healthy — keep publishing', tone: 'green' };
}

const REC_TONE: Record<string, string> = {
  red: 'bg-red-500/10 text-red-300 border-red-500/25',
  amber: 'bg-amber-500/10 text-amber-300 border-amber-500/25',
  green: 'bg-green-500/10 text-green-300 border-green-500/25',
  gray: 'bg-gray-500/10 text-gray-400 border-gray-600/30',
};

export default function SeoPage({ includeDomains, excludeDomains }: { includeDomains?: string[]; excludeDomains?: string[] } = {}) {
  const [sites, setSites] = useState<SiteOverview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState<'performance' | 'assistant'>('performance');

  const [period, setPeriod] = useState<string>('28d');
  const [customSince, setCustomSince] = useState<string>('');
  const [customUntil, setCustomUntil] = useState<string>('');
  const [sortKey, setSortKey] = useState<SortKey>('clicks');
  const [sortAsc, setSortAsc] = useState(false);

  const isCustom = period === 'custom';
  const customReady = isCustom && !!customSince && !!customUntil && customSince <= customUntil;
  const query = isCustom
    ? (customReady ? `since=${customSince}&until=${customUntil}` : null)
    : `days=${PERIODS.find(p => p.id === period)?.days ?? 28}`;

  const load = useCallback(() => {
    if (!query) return; // custom picked but dates not complete yet
    setLoading(true);
    setError(null);
    fetch(`/api/seo/overview?${query}`)
      .then(r => r.json())
      .then((data: any) => {
        if (data.error) { setError(data.error); setLoading(false); return; }
        let rows: SiteOverview[] = Array.isArray(data) ? data : [];
        // Project scoping (Boss 2026-07-07): props from /p/[slug] embed win;
        // ?sites=/?exclude= URL params as fallback for direct links.
        let inc = includeDomains, exc = excludeDomains;
        if (!inc && !exc && typeof window !== 'undefined') {
          const sp = new URLSearchParams(window.location.search);
          inc = sp.get('sites')?.split(',').filter(Boolean);
          exc = sp.get('exclude')?.split(',').filter(Boolean);
        }
        if (inc?.length) rows = rows.filter(s => inc!.includes(s.domain));
        else if (exc?.length) rows = rows.filter(s => !exc!.includes(s.domain));
        setSites(rows);
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [query]);

  useEffect(() => { load(); }, [load]);

  // REAL refresh (Boss 2026-07-08: "refresh must actually refresh"): pull
  // fresh GSC data server-side into seo.db, then reload. Throttled to 10 min
  // per domain server-side, so calling it on every open is safe.
  const syncScope = includeDomains; // undefined = all sites
  const syncAndLoad = useCallback((force = false) => {
    fetch('/api/seo/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ domains: syncScope, force }),
    })
      .then(r => r.json())
      .then(res => { if (res?.synced > 0) load(); })
      .catch(() => { /* sync is best-effort; db view already loaded */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(syncScope), load]);

  // AUTO-REFRESH ON OPEN + ON RETURN: sync when the page mounts and every
  // time the tab becomes visible again (Boss walks between pages — each
  // return re-pulls). Silent: table stays up while fresh data lands.
  useEffect(() => {
    syncAndLoad();
    const onVis = () => { if (!document.hidden) syncAndLoad(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [syncAndLoad]);

  // LIVE STATS (Boss 2026-07-07): silent refetch every 5 min while visible —
  // GSC data lands in the local db on the sync timer; the page keeps up
  // without a manual Refresh.
  useEffect(() => {
    const iv = setInterval(() => { if (!document.hidden) load(); }, 300_000);
    return () => clearInterval(iv);
  }, [load]);

  const withData = sites.filter(s => !s.noData);
  const noData = sites.filter(s => s.noData);
  const sorted = [
    ...withData.sort((a, b) => {
      const av = a[sortKey] as number, bv = b[sortKey] as number;
      // for position, lower is better — "descending" means best first
      const dir = sortAsc ? 1 : -1;
      return sortKey === 'avgPosition' ? (av - bv) * -dir : (av - bv) * dir;
    }),
    ...noData,
  ];

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-blue-600">
          <BarChart3 className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Performance · {sites.length} sites</h1>
          <p className="text-xs text-gray-500">Google Search Console</p>
        </div>
      </div>

      {selected ? (
        <div>
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <button onClick={() => setSelected(null)}
              className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white px-2.5 py-1.5 rounded-lg border border-white/[0.08] hover:border-gray-700">
              <ArrowLeft className="w-4 h-4" /> All sites
            </button>
            <h2 className="text-lg font-semibold text-white">{selected}</h2>
            <div className="ml-auto flex items-center gap-1 border border-white/[0.08] rounded-lg p-0.5">
              <button onClick={() => setDetailTab('performance')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${detailTab === 'performance' ? 'bg-cyan-500/15 text-cyan-400' : 'text-gray-500 hover:text-gray-300'}`}>
                <BarChart3 className="w-4 h-4" /> Performance
              </button>
              <button onClick={() => setDetailTab('assistant')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${detailTab === 'assistant' ? 'bg-cyan-500/15 text-cyan-400' : 'text-gray-500 hover:text-gray-300'}`}>
                <Sparkles className="w-4 h-4" /> Assistant
              </button>
            </div>
          </div>
          {detailTab === 'performance'
            ? <ProjectSeoTab domain={selected} />
            : <SeoAssistant domain={selected} overview={sites.find(s => s.domain === selected) || null} />}
        </div>
      ) : (
      <>
      {/* Controls: period pills · sort pills · refresh */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-1 bg-white/[0.04] border border-white/[0.08] rounded-lg p-1">
          {PERIODS.map(p => (
            <button key={p.id} onClick={() => setPeriod(p.id)}
              className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
                period === p.id ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-gray-300'}`}>
              {p.label}
            </button>
          ))}
          <button onClick={() => { setPeriod('custom'); if (!customSince || !customUntil) { setCustomSince(isoDaysAgo(30)); setCustomUntil(isoDaysAgo(3)); } }}
            className={`px-3 py-1 rounded-md text-xs font-semibold transition-colors ${
              isCustom ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-gray-300'}`}>
            Custom
          </button>
        </div>

        {isCustom && (
          <div className="flex items-center gap-1.5">
            <input type="date" value={customSince} max={customUntil || undefined}
              onChange={e => setCustomSince(e.target.value)}
              className="bg-[#0d1117]/85 border border-white/[0.08] rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:border-cyan-500/50 [color-scheme:dark]" />
            <span className="text-gray-600 text-xs">→</span>
            <input type="date" value={customUntil} min={customSince || undefined}
              onChange={e => setCustomUntil(e.target.value)}
              className="bg-[#0d1117]/85 border border-white/[0.08] rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:border-cyan-500/50 [color-scheme:dark]" />
          </div>
        )}

        <div className="flex items-center gap-1 ml-2">
          <span className="text-[10px] text-gray-600 uppercase tracking-wide mr-1">Sort by</span>
          {SORTS.map(s => (
            <button key={s.key} onClick={() => setSortKey(s.key)}
              className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                sortKey === s.key
                  ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-300'
                  : 'border-white/[0.08] text-gray-500 hover:text-gray-300'}`}>
              {s.label}
            </button>
          ))}
          <button onClick={() => setSortAsc(v => !v)}
            title="Flip sort direction"
            className="px-2.5 py-1 rounded-full text-xs border border-white/[0.08] text-gray-400 hover:text-gray-200">
            {sortAsc ? '↑ Low → High' : '↓ High → Low'}
          </button>
        </div>

        <button onClick={() => { load(); syncAndLoad(true); }}
          title="Re-pull fresh data from Google Search Console"
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/[0.08] bg-white/[0.04] text-xs text-gray-400 hover:text-white hover:border-gray-700 transition-colors">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center h-72">
          <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
        </div>
      )}

      {!loading && error && (
        <div className="p-6 rounded-xl border border-red-500/20 bg-red-500/5">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {!loading && !error && (
      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] text-gray-500 uppercase tracking-wide border-b border-white/[0.04]">
                <th className="text-left font-medium px-4 py-3">Site</th>
                <th className="text-left font-medium px-3 py-3">Status</th>
                <th className="text-right font-medium px-3 py-3">Clicks</th>
                <th className="text-right font-medium px-3 py-3">Impressions</th>
                <th className="text-right font-medium px-3 py-3">CTR</th>
                <th className="text-right font-medium px-3 py-3">Avg pos</th>
                <th className="text-left font-medium px-4 py-3">Recommendation</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(site => {
                const rec = recommend(site);
                return (
                  <tr key={site.domain}
                    className="border-b border-white/[0.04] hover:bg-white/[0.03] cursor-pointer transition-colors"
                    onClick={() => { setSelected(site.domain); setDetailTab(site.noData ? 'assistant' : 'performance'); }}>
                    {/* SITE — two-line: name + domain */}
                    <td className="px-4 py-3">
                      <div className="font-semibold text-white leading-tight">{siteName(site.domain)}</div>
                      <div className="text-[11px] text-gray-500">{site.domain}</div>
                    </td>
                    {/* STATUS pill */}
                    <td className="px-3 py-3">
                      {site.noData ? (
                        <span className="inline-block text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-gray-500/10 text-gray-400 border border-gray-600/30">Pending</span>
                      ) : (
                        <span className="inline-block text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-green-500/10 text-green-400 border border-green-500/25">Active</span>
                      )}
                    </td>
                    {site.noData ? (
                      <td colSpan={4} className="px-3 py-3 text-center text-xs text-gray-600">— no Search Console data yet —</td>
                    ) : (
                      <>
                        <td className="px-3 py-3 text-right">
                          <div className="flex items-baseline justify-end gap-1.5">
                            <span className="text-white font-semibold">{site.clicks.toLocaleString()}</span>
                            <ChangeUnder value={site.clicksChange} />
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <div className="flex items-baseline justify-end gap-1.5">
                            <span className="text-white font-semibold">{site.impressions.toLocaleString()}</span>
                            <ChangeUnder value={site.impressionsChange} />
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <div className="text-white font-semibold">{fmt(site.avgCtr)}%</div>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <div className="flex items-baseline justify-end gap-1.5">
                            <span className="text-white font-semibold">{fmt(site.avgPosition)}</span>
                            <ChangeUnder value={site.positionChange} suffix="" />
                          </div>
                        </td>
                      </>
                    )}
                    <td className="px-4 py-3">
                      <span className={`inline-block text-[11px] px-2 py-1 rounded-md border whitespace-nowrap ${REC_TONE[rec.tone]}`}>
                        {rec.text}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {!loading && !error && sites.length === 0 && (
        <div className="text-center py-20">
          <BarChart3 className="h-12 w-12 mx-auto text-gray-700 mb-4" />
          <p className="text-gray-500">No SEO data found</p>
        </div>
      )}
      </>
      )}
    </div>
  );
}
